/**
 * Selector multi-fecha para solicitud de vacaciones.
 * - Grid de calendario mensual con navegación prev/next.
 * - Click día → toggle selección.
 * - Diferencia visual: pasado, fin de semana, festivo, vacaciones aprobadas (no toca),
 *   vacaciones pendientes (no toca), día seleccionado.
 * - Sidebar dinámica con grupos consecutivos y contador de días facturables (que consumen vacaciones).
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, X, Send, Calendar as CalIcon, Info } from "lucide-react";

const MONTHS = [
  "enero","febrero","marzo","abril","mayo","junio",
  "julio","agosto","septiembre","octubre","noviembre","diciembre",
];
const DOW = ["L","M","X","J","V","S","D"];

const toISO = (d) => d.toISOString().slice(0, 10);
const parseISO = (s) => { const [y,m,d] = s.split("-").map(Number); return new Date(y, m-1, d); };
const isPast = (iso) => iso < new Date().toISOString().slice(0, 10);

// Agrupa fechas ISO consecutivas en periodos.
function groupConsecutive(datesISO) {
  const arr = [...datesISO].sort();
  const groups = [];
  for (const iso of arr) {
    const last = groups[groups.length - 1];
    if (last) {
      const next = new Date(parseISO(last.end)); next.setDate(next.getDate() + 1);
      if (toISO(next) === iso) { last.end = iso; last.count++; continue; }
    }
    groups.push({ start: iso, end: iso, count: 1 });
  }
  return groups;
}

export default function VacationRequestModal({ onClose, onCreated }) {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year,  setYear]  = useState(today.getFullYear());
  const [calendar, setCalendar] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [comment, setComment] = useState("");
  const [preview, setPreview] = useState({ billable_count: 0 });
  const [saving, setSaving] = useState(false);

  const loadCal = async () => {
    const r = await api.get("/portal/me/calendar", { params: { year, month } });
    setCalendar(r.data);
  };
  useEffect(() => { loadCal(); /* eslint-disable-next-line */ }, [year, month]);

  // Preview facturable cada vez que cambia la selección.
  useEffect(() => {
    if (selected.size === 0) { setPreview({ billable_count: 0, billable_days: [] }); return; }
    api.post("/portal/me/preview-days", { days: [...selected] })
      .then((r) => setPreview(r.data))
      .catch(() => {});
  }, [selected]);

  const toggle = (iso) => {
    if (isPast(iso)) return;
    const next = new Set(selected);
    if (next.has(iso)) next.delete(iso); else next.add(iso);
    setSelected(next);
  };

  const nav = (delta) => {
    let m = month + delta, y = year;
    if (m < 1)  { m = 12; y--; }
    if (m > 12) { m = 1;  y++; }
    setMonth(m); setYear(y);
  };

  const groups = useMemo(() => groupConsecutive([...selected]), [selected]);

  const submit = async () => {
    if (selected.size === 0) return toast.error("Selecciona al menos un día");
    setSaving(true);
    try {
      await api.post("/portal/me/vacation-requests", {
        days: [...selected],
        comment: comment.trim() || null,
      });
      toast.success("Solicitud enviada");
      onCreated && onCreated();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const cells = useMemo(() => {
    if (!calendar) return [];
    const out = [];
    const pad = (calendar.first_weekday + 6) % 7; // Lunes = 0
    for (let i = 0; i < pad; i++) out.push({ empty: true, key: `p${i}` });
    for (const d of calendar.days) out.push({ ...d, key: d.date });
    return out;
  }, [calendar]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="ds-card max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col" style={{ boxShadow: "var(--shadow-lg)" }} onClick={(e) => e.stopPropagation()} data-testid="vacation-modal">
        <div className="ds-card-h">
          <div className="t"><CalIcon size={14}/> Solicitar vacaciones</div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-[#F5F6F2] text-[#4B5158]" data-testid="vac-close"><X size={16}/></button>
        </div>

        <div className="flex-1 overflow-y-auto grid grid-cols-1 lg:grid-cols-[1fr_320px]">
          {/* CALENDAR */}
          <div className="p-5 border-r border-dashed border-[#D5D7CE]">
            <div className="flex items-center justify-between mb-3">
              <button className="ds-btn" style={{ padding: "6px 10px" }} onClick={() => nav(-1)} data-testid="vac-prev-month">
                <ChevronLeft size={13}/>
              </button>
              <div className="font-display font-black text-lg capitalize" data-testid="vac-month-label">
                {MONTHS[month-1]} {year}
              </div>
              <button className="ds-btn" style={{ padding: "6px 10px" }} onClick={() => nav(1)} data-testid="vac-next-month">
                <ChevronRight size={13}/>
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-1">
              {DOW.map((d) => (
                <div key={d} className="text-center font-mono text-[10px] uppercase tracking-widest text-[#8A9098] font-semibold py-1">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((c) => c.empty ? <div key={c.key}/> : (
                <DayCell
                  key={c.key}
                  cell={c}
                  isSelected={selected.has(c.date)}
                  onClick={() => toggle(c.date)}
                />
              ))}
            </div>

            {/* Legend */}
            <div className="mt-4 flex gap-3 flex-wrap text-[10.5px] text-[#4B5158]">
              <Legend color="#0F1210" label="Seleccionado" />
              <Legend color="#B4381F" label="Festivo" />
              <Legend color="#EEF0EA" label="Fin de semana" />
              <Legend color="#6D4AA6" label="Vac. aprobadas" />
              <Legend color="#D68A00" label="Vac. pendientes" />
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="p-5 space-y-4 bg-[#FBFBF8]">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[.14em] text-[#8A9098] font-semibold">Total seleccionados</div>
              <div className="font-display font-black text-3xl tracking-tight mt-1" data-testid="vac-total-selected">
                {selected.size} <span className="text-sm text-[#8A9098] font-medium">días</span>
              </div>
              <div className="mt-2 text-[12px]">
                Consumen vacaciones: <b className="text-[#3F5222]" data-testid="vac-billable-count">{preview.billable_count}</b>
              </div>
              {preview.billable_count < selected.size && (
                <div className="mt-1 text-[10.5px] text-[#8A9098] flex items-start gap-1">
                  <Info size={11} className="mt-[2px] flex-none"/>
                  <span>Los fines de semana y festivos no consumen días.</span>
                </div>
              )}
            </div>

            {groups.length > 0 && (
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[.14em] text-[#8A9098] font-semibold mb-1.5">Periodos</div>
                <ul className="space-y-1 text-[12.5px] max-h-40 overflow-y-auto" data-testid="vac-groups">
                  {groups.map((g, i) => (
                    <li key={i} className="flex justify-between items-center px-2 py-1 rounded-md bg-white border border-[#E6E7E1]">
                      <span className="font-mono">
                        {g.start === g.end ? fmt(g.start) : `${fmt(g.start)} → ${fmt(g.end)}`}
                      </span>
                      <span className="font-semibold text-[#3F5222]">{g.count}d</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <label className="font-mono text-[10px] uppercase tracking-[.14em] text-[#8A9098] font-semibold">Comentario (opcional)</label>
              <textarea rows={3} className="ds-textarea mt-1"
                        value={comment} onChange={(e) => setComment(e.target.value)}
                        placeholder="Añade una nota para el administrador…"
                        data-testid="vac-comment"/>
            </div>

            <div className="flex flex-col gap-2 pt-2">
              <button className="ds-btn primary w-full justify-center" onClick={submit} disabled={saving || selected.size === 0}
                      data-testid="vac-submit">
                <Send size={13}/> {saving ? "Enviando…" : "Solicitar vacaciones"}
              </button>
              <button className="ds-btn w-full justify-center" onClick={() => setSelected(new Set())} disabled={selected.size === 0}
                      data-testid="vac-clear">
                Limpiar selección
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DayCell({ cell, isSelected, onClick }) {
  const past = isPast(cell.date);
  const disabled = past || cell.is_approved_vac || cell.is_pending_vac;
  let bg = "#FFFFFF", ink = "#0F1210", border = "#E6E7E1", extra = "";
  if (cell.holiday)          { bg = "#FDEDE7"; ink = "#8C2B14"; border = "rgba(180,56,31,.25)"; }
  else if (cell.is_weekend)   { bg = "#EEF0EA"; ink = "#8A9098"; border = "#E0E2DA"; }
  if (cell.is_approved_vac)   { bg = "#F1EBFB"; ink = "#4E2E86"; border = "rgba(109,74,166,.3)"; }
  if (cell.is_pending_vac)    { bg = "#FFF7E6"; ink = "#8C5A00"; border = "rgba(214,138,0,.3)"; }
  if (isSelected)             { bg = "#0F1210"; ink = "#FFFFFF"; border = "#0F1210"; extra = "shadow-md"; }
  if (past && !isSelected)    { bg = "#F5F6F2"; ink = "#B3B3AF"; }

  const dayNum = Number(cell.date.slice(8, 10));
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={cell.holiday || (cell.is_approved_vac ? "Vacaciones aprobadas" : cell.is_pending_vac ? "Vacaciones pendientes" : "")}
      data-testid={`vac-day-${cell.date}`}
      className={`aspect-square flex flex-col items-center justify-center text-sm rounded-md border transition-all ${extra} ${disabled ? "cursor-not-allowed opacity-70" : "hover:scale-[1.04] hover:brightness-105 cursor-pointer"}`}
      style={{ background: bg, color: ink, borderColor: border }}
    >
      <span className={`font-mono ${isSelected ? "font-bold" : ""}`}>{dayNum}</span>
      {cell.holiday && !isSelected && <span className="text-[8px] mt-[1px] px-[2px] leading-tight truncate max-w-full">{cell.holiday.slice(0, 6)}</span>}
    </button>
  );
}

function Legend({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: color, border: `1px solid ${color === "#EEF0EA" || color === "#FFFFFF" ? "#D5D7CE" : "transparent"}` }}/>
      {label}
    </span>
  );
}

function fmt(iso) {
  const d = parseISO(iso);
  return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}`;
}
